import { shouldUseNopBalance, shouldUseNopProfitLoss } from '@/helpers/environmentHelper';
import { logToServer } from '@/services/LogService';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { setNopPositions, setNopPositionStatus } from '@/state/reducers/nopPositionSlice';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { WebSocketHook } from 'react-use-websocket/dist/lib/types';
import { toastLogout } from '../hooks/useToast';
import { Logger } from '../logger';

export interface NopPosition {
    instrumentId: number;
    instrumentName: string;
    instrumentWithDigitalAssetName?: string;
    amount: number;
    openPrice?: number;
    realizedProfitLoss: number;
    realizedProfitLossInBaseCurrency: number;
    unrealizedProfitLoss?: number;
    unrealizedProfitLossInBaseCurrency?: number;
    valueDate: string;
}

export interface Positions {
    cryptoCfd: NopPosition[];
    fx: NopPosition[];
    indexCfd: NopPosition[];
    futureCfd: NopPosition[];
    baseCurrency: string;
    isHeartBeat: boolean;
}

export interface PositionsContext extends Partial<WebSocketHook> {}

export interface PositionsProviderProps {
    children: React.ReactNode;
}

export const errorMessages = [
    'Session token is missing',
    'Session token is invalid',
    'Access token is no longer valid'
];

export const warnMessages = ['Celer Bridge is not available', 'Temporary internal error occurred'];

const nopPositionsURL = window.config?.positions?.websocket;

const PositionsContext = createContext({} as PositionsContext);
export const usePositions = () => useContext(PositionsContext);
export const PositionsProvider = (props: PositionsProviderProps) => {
    const dispatch = useAppDispatch();
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const didUnmount = useRef(false);

    const isMarginAccount = useMemo(() => {
        if (currentAccount && credentials) {
            const accountProperties = credentials.accounts.find(
                (account) => account.code === currentAccount
            )?.properties;
            return accountProperties?.find((property) => property.key === 'TYPE')?.value === 'MARGIN';
        }
    }, [currentAccount, credentials]);

    const wsURL = useMemo(() => {
        if (credentials && currentAccount && isMarginAccount && shouldUseNopBalance && shouldUseNopProfitLoss) {
            return `${nopPositionsURL}/${credentials.username}/${currentAccount}?sessionToken=${credentials.authToken}`;
        } else return null;
    }, [credentials, currentAccount]);

    const websocket = useWebSocket<Positions>(wsURL, {
        reconnectAttempts: 5,
        retryOnError: false,
        // heartbeat: {
        //     message: 'ping',
        //     returnMessage: 'pong',
        //     timeout: 30000, // 1 minute, if no response is received, the connection will be closed
        //     interval: 15000 // every 15 seconds, a ping message will be sent
        // },
        shouldReconnect: (closeEvent) => {
            return didUnmount.current === false;
        },
        onError: (e) => {
            logToServer(
                'warn',
                `User [${credentials?.username}, ${currentAccount}] 
                encountered error on PositionsProvider WebSocket ${JSON.stringify(e)}.`
            );
            Logger({
                title: `PositionsProvider WebSocket Error.`,
                callback: () => {
                    console.log(e);
                }
            });
        },
        onClose: (e) => {
            logToServer(
                'warn',
                `User [${credentials?.username}, ${currentAccount}] closed PositionsProvider WebSocket ${e?.code}, ${e?.reason}.`
            );
            Logger({
                title: `PositionsProvider WebSocket Closed.`,
                callback: () => {
                    console.log(e);
                }
            });
        }
    });

    const { readyState, lastJsonMessage, lastMessage, getWebSocket } = websocket;

    useEffect(() => {
        dispatch(setNopPositionStatus(ReadyState[readyState] as keyof typeof ReadyState));
    }, [readyState]);

    useEffect(() => {
        if (lastJsonMessage && !lastJsonMessage.isHeartBeat) dispatch(setNopPositions(lastJsonMessage));
    }, [lastJsonMessage]);

    useEffect(() => {
        if (errorMessages.includes(lastMessage?.data || '') && credentials) {
            Logger({
                title: `Inbound: NOP Positions Error - Message: ${lastMessage?.data || ''}.`,
                callback: () => {}
            });
            logToServer(
                'warn',
                `User [${credentials.username}] has been logged out due to NOP Positions error - Message: ${
                    lastMessage?.data || ''
                }`
            );
            toastLogout(credentials);
        } else if (warnMessages.includes(lastMessage?.data || '') && credentials) {
            Logger({
                title: `Inbound: NOP Positions Warning - Message: ${lastMessage?.data || ''}.`,
                callback: () => {}
            });
            logToServer(
                'warn',
                `User [${credentials.username}] is missing Cash Positions due to NOP Positions error - Message: ${
                    lastMessage?.data || ''
                }`
            );
        }
    }, [lastMessage, credentials]);

    const value = useMemo<PositionsContext>(() => {
        return { readyState, lastJsonMessage, getWebSocket };
    }, [readyState, lastJsonMessage, getWebSocket]);

    useEffect(() => {
        return () => {
            didUnmount.current = true;
        };
    }, []);

    return <PositionsContext.Provider value={value}>{props.children}</PositionsContext.Provider>;
};
